import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ConfigService } from '@rxap/config';
import { OPEN_API_OPERATION_ID, OPEN_API_SERVER_ID } from '@rxap/open-api';
import { catchError, from, of, switchMap, throwError } from 'rxjs';
import { RequestApiAuthService } from './request-api/request-api-auth.service';
import { resolveRequestApiAuthMethodFromConfig } from './request-api/request-api-connection';
import {
  legacyRequestApiServerFeatures,
  RequestApiFeatureService,
} from './request-api/request-api-features.service';
import { RequestApiOAuthService } from './request-api/request-api-oauth.service';
import { requestApiConnectionStore } from './stores/request-api-connection.store';

function setBearer(
  req: HttpRequest<unknown>,
  token: string | undefined,
): HttpRequest<unknown> {
  return req.clone({
    withCredentials: true,
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export const injectRequestApiAuthHttpInterceptorFn: HttpInterceptorFn = (
  req,
  next,
) => {
  const serverId = req.context.get(OPEN_API_SERVER_ID);

  if (serverId !== 'iirds') {
    return next(req);
  }

  const connection = inject(requestApiConnectionStore).activeConnection();
  const method =
    connection?.authMethod ??
    resolveRequestApiAuthMethodFromConfig(inject(ConfigService));

  switch (method) {
    case 'none': {
      return next(req);
    }
    case 'bearer': {
      const token =
        connection?.bearerToken ??
        inject(ConfigService).get<string>('api.iirds.auth.bearerToken');
      return next(setBearer(req, token));
    }
    case 'oauth2': {
      // never triggers a login redirect for a missing token — errors surface
      // to the caller. A rejected token (401/403), however, is invalidated so
      // it is not reused, and the user is forced to re-authenticate.
      const oauth = inject(RequestApiOAuthService);
      return oauth.getAccessToken$().pipe(
        switchMap((token) => next(setBearer(req, token))),
        catchError((error) => {
          if (
            error instanceof HttpErrorResponse &&
            (error.status === 401 || error.status === 403)
          ) {
            oauth.handleUnauthorized();
          }
          return throwError(() => error);
        }),
      );
    }
    case 'did':
    default: {
      const requestApiAuthService = inject(RequestApiAuthService);
      const featureService = inject(RequestApiFeatureService);
      const operationId = req.context.get(OPEN_API_OPERATION_ID);

      // register and verify only need the self-signed proof — routing them
      // through getValidJwt$ (which verifies/registers) would recurse.
      const alwaysProofOnly =
        operationId === 'AuthController_register' ||
        operationId === 'AuthController_verify';

      // servers without the ingest feature have no /auth/verify handshake —
      // they validate the self-signed proof directly, so every op is
      // proof-only there. A failing feature lookup falls back to the legacy
      // verify/register behavior (a probe hiccup must not fail the request).
      const jwt$ = alwaysProofOnly
        ? requestApiAuthService.getProofJwt$()
        : from(featureService.getFeatures()).pipe(
            catchError(() => of(legacyRequestApiServerFeatures())),
            switchMap((features) =>
              features.ingest
                ? requestApiAuthService.getValidJwt$()
                : requestApiAuthService.getProofJwt$(),
            ),
          );

      return jwt$.pipe(switchMap((jwt) => next(setBearer(req, jwt))));
    }
  }
};
